import { useEffect, useState, useCallback } from "react";
import API from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { ListChecks, Trash } from "@phosphor-icons/react";

/** Summary stats for a job's embedded checklist. */
export function checklistStats(job) {
  const items = job?.checklist?.items || [];
  const done = items.filter((i) => i.done).length;
  const requiredOutstanding = items.filter((i) => i.required && !i.done).length;
  return { total: items.length, done, outstanding: items.length - done, requiredOutstanding };
}

/**
 * Shared job checklist card. Admins can attach/remove a template; admins,
 * the assigned supplier and the assigned operative can tick items.
 */
export default function JobChecklist({ job, onChanged, canTick = true, isAdmin = false }) {
  const checklist = job?.checklist;
  const hasItems = !!(checklist && (checklist.items || []).length);
  const [templates, setTemplates] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [attaching, setAttaching] = useState(false);

  useEffect(() => {
    if (isAdmin && !hasItems) {
      API.get("/checklist-templates")
        .then(({ data }) => setTemplates((data.templates || []).filter((t) => t.active !== false)))
        .catch(() => {});
    }
  }, [isAdmin, hasItems]);

  const toggle = useCallback(async (item) => {
    if (!canTick || busyId) return;
    setBusyId(item.id);
    try {
      await API.put(`/jobs/${job.id}/checklist/items/${item.id}`, { done: !item.done });
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update item");
    } finally {
      setBusyId(null);
    }
  }, [canTick, busyId, job?.id, onChanged]);

  const attach = async (templateId) => {
    if (!templateId || templateId === "__none__") return;
    setAttaching(true);
    try {
      await API.post(`/jobs/${job.id}/checklist/attach`, { template_id: templateId });
      toast.success("Checklist attached");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not attach checklist");
    } finally {
      setAttaching(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove this checklist from the job?")) return;
    try {
      await API.delete(`/jobs/${job.id}/checklist`);
      toast.success("Checklist removed");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove checklist");
    }
  };

  if (!hasItems) {
    if (!isAdmin || templates.length === 0) return null;
    return (
      <div className="border border-dashed border-border rounded-md p-4 bg-card" data-testid="job-checklist-attach">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
          <ListChecks size={14} /> Checklist
        </h3>
        <Select onValueChange={attach} disabled={attaching}>
          <SelectTrigger className="h-8 text-xs" data-testid="job-checklist-template-select">
            <SelectValue placeholder="Attach a checklist template..." />
          </SelectTrigger>
          <SelectContent>
            {templates.map((t) => (
              <SelectItem key={t.id} value={t.id}>{t.name} ({t.items?.length || 0} items)</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  const items = checklist.items || [];
  const done = items.filter((i) => i.done).length;
  const pct = items.length ? Math.round((done / items.length) * 100) : 0;

  return (
    <div className="border border-border rounded-md p-4 bg-card" data-testid="job-checklist">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
          <ListChecks size={14} /> Checklist
        </h3>
        <div className="flex items-center gap-2">
          <span className={`text-[10px] font-semibold ${done === items.length ? "text-emerald-600" : "text-muted-foreground"}`} data-testid="job-checklist-progress-label">
            {done}/{items.length}
          </span>
          {isAdmin && (
            <button onClick={remove} className="text-muted-foreground hover:text-red-500" title="Remove checklist" data-testid="job-checklist-remove">
              <Trash size={13} />
            </button>
          )}
        </div>
      </div>
      {checklist.template_name && (
        <p className="text-[10px] text-muted-foreground mb-2">{checklist.template_name}</p>
      )}
      <div className="h-1.5 rounded-full bg-muted overflow-hidden mb-3">
        <div
          className={`h-full rounded-full transition-all ${done === items.length ? "bg-emerald-500" : "bg-primary"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <ul className="space-y-1.5">
        {items.map((item) => (
          <li key={item.id}>
            <button
              type="button"
              onClick={() => toggle(item)}
              disabled={!canTick || busyId === item.id}
              className={`w-full flex items-start gap-2 text-left rounded px-1.5 py-1 transition-colors ${canTick ? "hover:bg-muted/50 cursor-pointer" : "cursor-default"}`}
              data-testid={`checklist-item-${item.id}`}
            >
              <span className={`mt-0.5 h-4 w-4 shrink-0 rounded border flex items-center justify-center text-[10px] ${item.done ? "bg-emerald-500 border-emerald-500 text-white" : "border-border bg-background"}`}>
                {item.done ? "✓" : ""}
              </span>
              <span className="min-w-0">
                <span className={`text-xs block ${item.done ? "line-through text-muted-foreground" : ""}`}>
                  {item.text}
                  {item.required && !item.done && <span className="text-red-500 ml-1" title="Required">*</span>}
                </span>
                {item.done && item.done_by_name && (
                  <span className="text-[10px] text-muted-foreground block">
                    {item.done_by_name} · {item.done_at ? new Date(item.done_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
                  </span>
                )}
              </span>
            </button>
          </li>
        ))}
      </ul>
      {items.some((i) => i.required && !i.done) && (
        <p className="text-[10px] text-amber-600 mt-2" data-testid="checklist-required-note">* required before completion (warning only)</p>
      )}
    </div>
  );
}
